import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons/faClipboardList";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Section } from "../../../components/Section";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { FontSize, Text } from "../../../components/Text";
import { AtiraSpacePlan } from "../../../model/plan/Plan";
import { AtiraSpacePlanType } from "../../../model/plan/types/AtiraSpacePlanType.enum";
import { ThresholdKinds } from "../../../model/threshold/types/ThresholdKinds.enum";
import { paymentActions } from "../../../redux/payment/payment.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { thresholdSliceSelectors } from "../../../redux/threshold/threshold.selector";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { standardDate } from "../../../utils/Date";
import { getPlanTitle, toUSD } from "../../../utils/pricingUtils";
import camelCase from "lodash/camelCase";
import omit from "lodash/omit";
import { EntityIcons } from "../../../utils/GlobalIcons";

const getPercentageColor = (percentage: number) => {
  if (percentage >= 75) {
    return "danger";
  } else if (percentage >= 50) {
    return "warning";
  } else {
    return "success";
  }
};

const thresholdIcons: Partial<Record<ThresholdKinds, IconProp>> = {
  USERS: faUsers,
  ENTRIES: EntityIcons.Entries,
  FORMS: EntityIcons.Forms,
  KANBANS: EntityIcons.Deals,
  CUSTOM_FIELDS: faClipboardList,
};

const SpanBold = styled.span`
  font-weight: bold;
`;

const ThresholdCard = styled(Flex)<{ percentage?: number }>`
  min-width: 12rem;
  flex-direction: column;
  padding: ${Spacing.m};
  border-radius: ${Rounded.md};
  background-color: ${({ percentage, theme }) =>
    theme[getPercentageColor(percentage || 0)]}11;
  border: 1px solid
    ${({ percentage, theme }) => theme[getPercentageColor(percentage || 0)]};
  text-align: center;
`;

const ThresholdTitle = styled(Text)`
  font-size: ${FontSize.m};
  color: ${({ theme }) => theme.darkerSub};
  margin-bottom: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
`;

const ThresholdValue = styled(Text)<{ percentage?: number }>`
  font-weight: bold;
  color: ${({ percentage, theme }) =>
    theme[getPercentageColor(percentage || 0)]};
  text-align: center;
`;

const ThresholdDivider = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  margin: 0 ${Spacing.xs};
`;

export const SubscriptionCurrentSubscriptionTab: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const plan = user?.plan || ({} as AtiraSpacePlan);
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );
  const thresholdsLoading = useAppSelector(
    thresholdSliceSelectors.selectThresholdLoading,
  );

  const usedThresholds = thresholds?.consumed;
  const totalThresholds = thresholds?.total;

  const onUpdatePaymentMethod = async () => {
    try {
      setLoading(true);
      const url = await dispatch(
        paymentActions.getUpdatePaymentMethodURL(),
      ).unwrap();
      window.createLemonSqueezy();
      window.LemonSqueezy.Url.Open(url);
    } catch (e: any) {
      console.error("Failed to update payment method:", e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(thresholdActions.getThresholds({ userId: user._id! }));
  }, [dispatch, user._id]);

  if (isEmpty(plan) || thresholdsLoading) {
    return <SpinnerFullScreen containerHeight={"70vh"} />;
  }

  return (
    <Flex flexDirection="column" gap="m" flexGrow={1}>
      <Section title={t("subscription.plan.title")}>
        <Text color="darkerSub">{t("subscription.plan.description")}</Text>

        <Flex flexDirection="column" gap="s">
          <Text>
            <SpanBold>{t("subscription.plan.title")}: </SpanBold>
            {t(getPlanTitle(plan.type))}
          </Text>

          <Text>
            <Trans
              i18nKey={"subscription.status"}
              values={{
                status: t(
                  `subscription.status.${plan.lemonSubscriptionData?.subscription.status || "not_subscribed"}`,
                ),
              }}
              components={[<SpanBold />]}
            />
          </Text>

          {plan.lemonSubscriptionData?.subscription ? (
            <Info
              info={
                <Trans
                  i18nKey={`subscription.status.${plan.lemonSubscriptionData?.subscription.status}.description`}
                  values={{
                    amount: toUSD(
                      Number(plan.lemonSubscriptionData?.variant?.price!),
                    ),
                    date: standardDate(plan.lemonPlanData?.renewsAt!),
                    last4:
                      plan.lemonSubscriptionData?.subscription.card_last_four,
                  }}
                  components={[<SpanBold />]}
                />
              }
            />
          ) : null}
        </Flex>
      </Section>

      <Section title={t("subscription.plan.features.title")}>
        <Text color="darkerSub">
          {t("subscription.plan.features.description")}
        </Text>

        <Flex gap="m" flexWrap="wrap">
          {Object.entries(
            omit(totalThresholds, ThresholdKinds.MOBILE_APP) || {},
          ).map(([key, value]) => {
            const used =
              usedThresholds?.[key as keyof typeof usedThresholds] || 0;
            const percentage = (used / (value as number)) * 100;

            return (
              <ThresholdCard key={key} percentage={percentage}>
                <ThresholdTitle>
                  {t(`common.${camelCase(key)}`)}

                  <AtiraIcon
                    icon={thresholdIcons[key as keyof typeof thresholdIcons]!}
                  />
                </ThresholdTitle>
                <ThresholdValue percentage={percentage}>
                  {used} <ThresholdDivider>/</ThresholdDivider>
                  {new Intl.NumberFormat("en-IN", {
                    maximumSignificantDigits: 3,
                  }).format(value)}
                </ThresholdValue>
              </ThresholdCard>
            );
          })}
        </Flex>
      </Section>

      {plan.type !== AtiraSpacePlanType.PLAN_0 ? (
        <Section title={t("subscription.page.payment.title")}>
          <Text color="darkerSub">
            {t("subscription.page.payment.description")}
          </Text>

          <Button
            loading={loading}
            height="2.5rem"
            width="17rem"
            onClick={onUpdatePaymentMethod}
          >
            {t("subscription.page.payment.button")}
          </Button>
        </Section>
      ) : null}
    </Flex>
  );
};
